import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DropboxChooser from 'react-dropbox-chooser';
import { addDoc, collection, deleteDoc, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { FiAlertCircle, FiClipboard, FiEdit2, FiExternalLink, FiEye, FiPlus, FiSearch, FiTrash2, FiUploadCloud } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { allows, rightsOf, unitsOf, useSessionUser } from '../../lib/permissions';
import { formatDate, toInputDate } from '../../lib/format';
import { archiveDocuments, documentError, sendFiles } from '../../lib/documents';
import AttachmentsField, { useDocumentsStatus } from '../documents/AttachmentsField';
import DocumentViewer from '../documents/DocumentViewer';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, EmptyState, Field, INPUT, Modal, Notice, PageHeader, Pill, Segmented, Select, SIZE, SkeletonRows, TextInput } from '../ui/kit';
import PatientPicker from '../patients/PatientPicker';
import { UNITS } from '../patients/patientModel';

// Dropbox app of the service (public key, same as the former staff page).
const DROPBOX_APP_KEY = '2ejuqgnngalbk6n';
const EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.gif', '.pptx', '.svg', '.jpeg', '.jpg', '.png'];
const TYPES = ['Normal', 'Mission Canadienne', 'Mission Suisse'];
const TYPE_OPTIONS = [...TYPES.map((type) => ({ value: type, label: type })), { value: 'Autres', label: 'Autre' }];

const typeOf = (item) => (TYPES.includes(item.commentType) ? item.commentType : item.commentType === 'Autres' ? 'Autres' : TYPES.includes(item.comment) ? item.comment : 'Autres');
const typeLabel = (item) => (typeOf(item) === 'Autres' ? item.comment || 'Autre' : typeOf(item));
const monthLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date non précisée';
  const text = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_FORM = { date: today(), type: 'Normal', comment: '', unit: '', patientIds: [] };
// Documents drawn at once; the rest comes with "Afficher plus" (hundreds of staff otherwise
// slow down every filter change).
const PAGE_SIZE = 30;

const StaffRow = memo(({ entry, patientsById, editable, deletable, onOpen, onEdit, onDelete }) => {
  const { item } = entry;
  return (
    <li className="flex flex-col gap-[12px] border-0 border-b-[1px] border-solid border-black/[0.05] px-[16px] py-[16px] last:border-b-0 sm:flex-row sm:items-center sm:px-6">
      <span className="hidden h-10 w-10 flex-none items-center justify-center rounded-xl bg-teal-50 text-teal-700 sm:flex">
        <FiClipboard size={18} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="m-0 break-words text-[15px] font-semibold text-ink">{item.name || 'Document sans nom'}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[13px] text-black/45">
          <span>{entry.dateLabel || 'Date non précisée'}</span>
          <Pill tone={entry.type === 'Normal' ? 'neutral' : 'brand'}>{typeLabel(item)}</Pill>
          {item.service && <Pill>{item.service}</Pill>}
        </div>
        {(item.patientIds || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {item.patientIds.map((patientId) => {
              const patient = patientsById[patientId];
              return patient ? (
                <Link
                  key={patientId}
                  to={`/patients/${patientId}`}
                  className="inline-flex h-7 items-center rounded-full bg-brand-50 px-[10px] text-[12.5px] font-medium text-brand-700 no-underline transition hover:bg-brand-100 hover:text-brand-700"
                >
                  {patient.nom}
                </Link>
              ) : (
                <Pill key={patientId}>Dossier introuvable</Pill>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex flex-none items-center gap-1">
        {item.document && item.document.id ? (
          <button type="button" onClick={() => onOpen(item)} className={`${BUTTON.secondary} ${SIZE.sm}`}>
            <FiEye size={14} aria-hidden="true" />
            Ouvrir
          </button>
        ) : (
          item.link && (
            <a href={item.link} target="_blank" rel="noopener noreferrer" className={`${BUTTON.secondary} ${SIZE.sm}`}>
              <FiExternalLink size={14} aria-hidden="true" />
              Ouvrir
            </a>
          )
        )}
        {editable && (
          <button type="button" onClick={() => onEdit(item)} aria-label={`Modifier ${item.name}`} title="Modifier" className={`${BUTTON.ghost} h-9 w-9 p-0 text-black/50`}>
            <FiEdit2 size={15} aria-hidden="true" />
          </button>
        )}
        {deletable && (
          <button
            type="button"
            onClick={() => onDelete(item)}
            aria-label={`Supprimer ${item.name}`}
            title="Supprimer"
            className={`${BUTTON.ghost} h-9 w-9 p-0 text-red-500 hover:bg-red-50 hover:text-red-600`}
          >
            <FiTrash2 size={15} aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
});

const StaffPage = () => {
  const user = useSessionUser();
  const rights = rightsOf(user);
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading');
  const [search, setSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dialog, setDialog] = useState(null); // { mode: 'add' | 'edit' | 'delete', item }
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [notice, setNotice] = useState('');
  // Files chosen on the device for a new staff, and the staff document opened.
  const [pending, setPending] = useState([]);
  const [sending, setSending] = useState(null);
  const [viewing, setViewing] = useState(null);
  const documentsStatus = useDocumentsStatus();

  const isAdmin = user.role === 'Admin';
  const myUnits = unitsOf(user.service);
  const ownsItem = (item) => isAdmin || unitsOf(item.service).some((unit) => myUnits.includes(unit));
  const canAdd = allows(rights, 'staff', 'modifier');
  const canEdit = (item) => allows(rights, 'staff', 'modifier') && ownsItem(item);
  const canDelete = (item) => allows(rights, 'staff', 'supprimer') && ownsItem(item);

  useEffect(() => {
    ensurePatients().catch(() => {});
    return onSnapshot(
      collection(db, 'filesObs'),
      (snapshot) => {
        setItems(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setStatus('ready');
      },
      () => setStatus('error')
    );
  }, [ensurePatients]);

  const patientsById = useMemo(() => Object.fromEntries(patients.map((patient) => [patient._id, patient])), [patients]);

  // Staff of the person's own units (all units for the administration).
  const visible = useMemo(
    () => items.filter((item) => isAdmin || unitsOf(item.service).some((unit) => myUnits.includes(unit))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, isAdmin, user.service]
  );
  const unitsInData = useMemo(() => [...new Set(visible.map((item) => item.service).filter(Boolean))].sort(), [visible]);

  // Dates, types and search text computed once per change of the data, not at every click.
  const prepared = useMemo(
    () =>
      visible
        .map((item) => {
          const dateLabel = formatDate(item.timestamp);
          const names = (item.patientIds || []).map((patientId) => (patientsById[patientId] || {}).nom).join(' ');
          return {
            item,
            time: new Date(item.timestamp || 0).getTime() || 0,
            type: typeOf(item),
            month: monthLabel(item.timestamp),
            dateLabel,
            search: [item.name, item.comment, dateLabel, names].join(' ').toLowerCase(),
          };
        })
        .sort((a, b) => b.time - a.time),
    [visible, patientsById]
  );

  // The list follows the typing without holding the keyboard back.
  const query = useDeferredValue(search.trim().toLowerCase());
  const shown = useMemo(
    () =>
      prepared.filter(
        (entry) =>
          (!unitFilter || entry.item.service === unitFilter) &&
          (!typeFilter || entry.type === typeFilter) &&
          (!query || entry.search.includes(query))
      ),
    [prepared, unitFilter, typeFilter, query]
  );

  const [limit, setLimit] = useState(PAGE_SIZE);
  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [unitFilter, typeFilter, query]);

  const groups = useMemo(() => {
    const result = [];
    shown.slice(0, limit).forEach((entry) => {
      const last = result[result.length - 1];
      if (last && last.label === entry.month) last.entries.push(entry);
      else result.push({ label: entry.month, entries: [entry] });
    });
    return result;
  }, [shown, limit]);

  const openAdd = () => {
    setPending([]);
    setForm({ ...EMPTY_FORM, date: today(), unit: isAdmin || myUnits.length > 1 ? '' : user.service || '' });
    setDialogError('');
    setDialog({ mode: 'add' });
  };
  const openEdit = useCallback((item) => {
    setForm({
      date: toInputDate(item.timestamp) || today(),
      type: typeOf(item),
      comment: typeOf(item) === 'Autres' ? item.comment || '' : '',
      unit: item.service || '',
      patientIds: item.patientIds || [],
    });
    setDialogError('');
    setDialog({ mode: 'edit', item });
  }, []);
  const openDelete = useCallback((item) => {
    setDialogError('');
    setDialog({ mode: 'delete', item });
  }, []);
  const close = () => {
    if (!busy) setDialog(null);
  };

  const metadata = () => ({
    timestamp: form.date,
    commentType: form.type,
    comment: form.type === 'Autres' ? form.comment.trim() : form.type,
    patientIds: form.patientIds,
  });

  // One staff entry per document. { name, link }: file chosen in a personal Dropbox (former
  // chooser, until the service's Dropbox is linked); { name, document }: file of the service's Dropbox.
  const entryFor = (file) => ({
    name: file.name,
    ...(file.document ? { document: file.document } : { link: file.link }),
    ...metadata(),
    service: form.unit || user.service || '',
    createdBy: user.name,
    createdAt: new Date().toISOString(),
  });

  const addFiles = async (files) => {
    setBusy(true);
    setDialogError('');
    try {
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        await addDoc(collection(db, 'filesObs'), entryFor(file));
      }
      setDialog(null);
      setNotice(`${files.length} document${files.length > 1 ? 's' : ''} de staff ajouté${files.length > 1 ? 's' : ''}.`);
    } catch (error) {
      setDialogError('Les documents n’ont pas pu être enregistrés. Réessayez.');
    } finally {
      setBusy(false);
    }
  };

  // Files chosen on the device: sent to the staff folder of the service's Dropbox, then listed.
  // After a failure the dialog stays open with the files not yet sent.
  const sendAndAdd = async () => {
    setBusy(true);
    setDialogError('');
    let sent = [];
    let failure = null;
    try {
      sent = await sendFiles(pending, { section: 'staff', unit: form.unit || user.service || '' }, setSending);
    } catch (error) {
      sent = error.sent || [];
      failure = error;
    } finally {
      setSending(null);
    }
    setPending((current) => current.slice(sent.length));
    try {
      for (const item of sent) {
        // eslint-disable-next-line no-await-in-loop
        await addDoc(collection(db, 'filesObs'), entryFor({ name: item.name, document: item }));
      }
    } catch (error) {
      failure = failure || error;
    }
    if (failure) {
      setDialogError(await documentError(failure, 'Un document n’a pas pu être enregistré. Réessayez.'));
    } else {
      setDialog(null);
      setNotice(`${sent.length} document${sent.length > 1 ? 's' : ''} de staff ajouté${sent.length > 1 ? 's' : ''}.`);
    }
    setBusy(false);
  };

  const saveEdit = async () => {
    setBusy(true);
    setDialogError('');
    try {
      await updateDoc(doc(db, 'filesObs', dialog.item.id), { ...metadata(), updatedBy: user.name, updatedAt: new Date().toISOString() });
      setDialog(null);
      setNotice('Document de staff mis à jour.');
    } catch (error) {
      setDialogError('La modification n’a pas pu être enregistrée.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setDialogError('');
    try {
      // A file of the service's Dropbox goes to its archives first (never erased).
      if (dialog.item.document && dialog.item.document.id) {
        const failed = await archiveDocuments([dialog.item.document]);
        if (failed.length) throw new Error('archive');
      }
      await deleteDoc(doc(db, 'filesObs', dialog.item.id));
      setDialog(null);
      setNotice('Document de staff supprimé.');
    } catch (error) {
      setDialogError(error.message === 'archive' ? 'Le document n’a pas pu être retiré du Dropbox du service. Réessayez.' : 'Le document n’a pas pu être supprimé.');
    } finally {
      setBusy(false);
    }
  };

  const formInvalid = !form.date || (form.type === 'Autres' && !form.comment.trim()) || (dialog && dialog.mode === 'add' && (isAdmin || myUnits.length > 1) && !form.unit);

  const typeChip = (value, label) => {
    const active = typeFilter === value;
    return (
      <button
        key={value || 'all'}
        type="button"
        aria-pressed={active}
        onClick={() => setTypeFilter(value)}
        className={`inline-flex h-9 flex-none cursor-pointer items-center whitespace-nowrap rounded-full border-0 px-[14px] text-[13.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
          active ? 'bg-ink text-white' : 'bg-white text-black/65 shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:text-ink'
        }`}
      >
        {label}
      </button>
    );
  };

  const unitChoices = isAdmin ? UNITS : myUnits.length > 1 ? UNITS.filter((unit) => myUnits.includes(unit.toLowerCase())) : [];

  const formFields = (
    <div className="flex flex-col gap-[18px]">
      <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-2">
        <Field id="staff-date" label="Date du staff" required>
          <TextInput id="staff-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
        {unitChoices.length > 0 && (
          <Field id="staff-unit" label="Unité" required={dialog && dialog.mode === 'add'}>
            <Select id="staff-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} options={form.unit && !unitChoices.includes(form.unit) ? [form.unit, ...unitChoices] : unitChoices} />
          </Field>
        )}
      </div>
      <Field id="staff-type" label="Type de staff">
        <Segmented id="staff-type" value={form.type} onChange={(value) => setForm({ ...form, type: value })} options={TYPE_OPTIONS} allowEmpty={false} />
      </Field>
      {form.type === 'Autres' && (
        <Field id="staff-comment" label="Précisez" required>
          <TextInput id="staff-comment" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="ex. Staff commun avec la cardiologie" />
        </Field>
      )}
      <Field id="staff-patients" label="Patients concernés" hint="Le document apparaîtra dans la fiche de chacun de ces patients.">
        <PatientPicker id="staff-patients" values={form.patientIds} onChange={(patientIds) => setForm({ ...form, patientIds })} />
      </Field>
      {dialogError && <Notice tone="red">{dialogError}</Notice>}
    </div>
  );

  return (
    <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        eyebrow="Soins"
        title="Staff"
        subtitle={
          status === 'ready'
            ? `${shown.length} document${shown.length > 1 ? 's' : ''} · ${isAdmin ? 'toutes les unités' : `unité ${user.service || 'non précisée'}`}`
            : 'Chargement des documents…'
        }
        actions={
          canAdd && (
            <button type="button" onClick={openAdd} className={`${BUTTON.primary} ${SIZE.md}`}>
              <FiPlus size={16} aria-hidden="true" />
              Ajouter un staff
            </button>
          )
        }
      />

      {notice && (
        <div className="mt-6">
          <Notice tone="green">{notice}</Notice>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-[12px]">
        <div className="relative">
          <FiSearch size={17} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom du document, date, patient, type…"
            aria-label="Rechercher un staff"
            className={`${INPUT} h-11 pl-[42px]`}
          />
        </div>
        <div className="flex flex-col gap-[12px] lg:flex-row lg:items-center lg:justify-between">
          <div className="-mx-[20px] flex gap-2 overflow-x-auto px-[20px] pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden">
            {typeChip('', 'Tous')}
            {TYPE_OPTIONS.map((option) => typeChip(option.value, option.value === 'Autres' ? 'Autres' : option.label))}
          </div>
          {unitsInData.length > 1 && (
            <Segmented
              value={unitFilter}
              onChange={setUnitFilter}
              allowEmpty={false}
              options={[{ value: '', label: 'Toutes les unités' }, ...unitsInData.map((unit) => ({ value: unit, label: unit }))]}
            />
          )}
        </div>
      </div>

      <div className="mt-[20px] flex flex-col gap-6">
        {status === 'loading' ? (
          <div className={`${CARD} p-6`}>
            <SkeletonRows rows={5} />
          </div>
        ) : status === 'error' ? (
          <div className={CARD}>
            <EmptyState icon={FiAlertCircle} title="Documents indisponibles">
              Les documents de staff n’ont pas pu être chargés. Vérifiez la connexion, puis rechargez la page.
            </EmptyState>
          </div>
        ) : groups.length === 0 ? (
          <div className={CARD}>
            <EmptyState icon={FiClipboard} title="Aucun document de staff">
              {visible.length ? 'Aucun document ne correspond à ces filtres.' : 'Les documents de staff de votre unité apparaîtront ici.'}
            </EmptyState>
          </div>
        ) : (
          <>
            {groups.map((group) => (
              <section key={group.label}>
                <h2 className="mb-[10px] mt-0 px-1 text-[13px] font-semibold uppercase tracking-[0.06em] text-black/40">{group.label}</h2>
                <ul className={`${CARD} m-0 list-none overflow-hidden p-0`}>
                  {group.entries.map((entry) => (
                    <StaffRow
                      key={entry.item.id}
                      entry={entry}
                      patientsById={patientsById}
                      editable={canEdit(entry.item)}
                      deletable={canDelete(entry.item)}
                      onOpen={setViewing}
                      onEdit={openEdit}
                      onDelete={openDelete}
                    />
                  ))}
                </ul>
              </section>
            ))}
            {limit < shown.length && (
              <div className="flex flex-col items-center gap-1 text-center">
                <button type="button" onClick={() => setLimit((count) => count + PAGE_SIZE)} className={`${BUTTON.ghost} ${SIZE.md}`}>
                  Afficher {Math.min(PAGE_SIZE, shown.length - limit)} documents de plus
                </button>
                <span className="text-[12.5px] text-black/40">
                  {limit} sur {shown.length} documents affichés
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        open={Boolean(dialog && dialog.mode === 'add')}
        title="Ajouter un staff"
        onClose={close}
        size="lg"
        footer={
          <>
            <button type="button" onClick={close} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            {documentsStatus.ready ? (
              <button type="button" onClick={sendAndAdd} disabled={formInvalid || busy || !pending.length} className={`${BUTTON.primary} ${SIZE.md}`}>
                {sending ? `Envoi ${sending.index + 1}/${pending.length}…` : busy ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            ) : formInvalid || busy ? (
              <button type="button" disabled className={`${BUTTON.primary} ${SIZE.md}`}>
                <FiUploadCloud size={16} aria-hidden="true" />
                {busy ? 'Enregistrement…' : 'Choisir les fichiers dans Dropbox'}
              </button>
            ) : (
              <DropboxChooser appKey={DROPBOX_APP_KEY} success={addFiles} multiselect extensions={EXTENSIONS}>
                <button type="button" className={`${BUTTON.primary} ${SIZE.md} w-full`}>
                  <FiUploadCloud size={16} aria-hidden="true" />
                  Choisir les fichiers dans Dropbox
                </button>
              </DropboxChooser>
            )}
          </>
        }
      >
        <p className="mb-[18px] mt-0 text-[14px] leading-relaxed text-black/50">
          {documentsStatus.ready
            ? 'Renseignez le staff, ajoutez ses documents depuis cet appareil, puis enregistrez.'
            : 'Renseignez le staff, puis choisissez ses documents dans Dropbox : ils sont enregistrés dès la sélection.'}
        </p>
        {formFields}
        {documentsStatus.ready && (
          <div className="mt-[18px]">
            <Field id="staff-files" label="Documents" required>
              <AttachmentsField id="staff-files" status={documentsStatus} pending={pending} onPendingChange={setPending} sending={sending} />
            </Field>
          </div>
        )}
      </Modal>

      {viewing && (
        <DocumentViewer
          item={viewing.document}
          onClose={() => setViewing(null)}
          canReplace={canEdit(viewing)}
          canDownload={allows(rights, 'staff', 'tout')}
        />
      )}

      <Modal
        open={Boolean(dialog && dialog.mode === 'edit')}
        title="Modifier le staff"
        onClose={close}
        size="lg"
        footer={
          <>
            <button type="button" onClick={close} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={saveEdit} disabled={formInvalid || busy} className={`${BUTTON.primary} ${SIZE.md}`}>
              {busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      >
        {dialog && dialog.item && <p className="mb-[18px] mt-0 break-words text-[14px] font-medium text-ink">{dialog.item.name}</p>}
        {formFields}
      </Modal>

      <Modal
        open={Boolean(dialog && dialog.mode === 'delete')}
        title="Supprimer le document"
        onClose={close}
        footer={
          <>
            <button type="button" onClick={close} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Suppression…' : 'Supprimer'}
            </button>
          </>
        }
      >
        <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
          Le document <strong className="text-ink">{dialog && dialog.item && dialog.item.name}</strong> sera retiré du staff.{' '}
          {dialog && dialog.item && dialog.item.document ? 'Il reste conservé dans les archives du Dropbox du service.' : 'Le fichier reste dans Dropbox.'}
        </p>
        {dialogError && (
          <div className="mt-[12px]">
            <Notice tone="red">{dialogError}</Notice>
          </div>
        )}
      </Modal>
    </main>
  );
};

export default StaffPage;
